import { fmtDisplayDate } from '@utils/displayDate'

/**
 * Column derivation + value formatting for the auto-detecting specialized-report
 * tables (used by AutoReportTable and AutoReportGrid). Kept in a plain module
 * (no React component) so importing these helpers doesn't trip
 * react-refresh/only-export-components on the component files.
 */

export const fmtMoney = (v) => {
  const n = Number(v ?? 0)
  if (n === 0) return ''
  const fmt = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `($${fmt})` : `$${fmt}`
}
export const fmtNumber = (v, d = 2) => {
  const n = Number(v ?? 0)
  if (!isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
}
// Himaad UI feedback, 2026-08-27: was a local unpadded 'M/D/YYYY' copy; the whole
// of Report Center chrome now reads MM/DD/YYYY from ONE formatter
// (src/utils/displayDate.js), matching UsDateInput. Display only — nothing here
// reaches CB, and these cells are cb2's own synthesis, not CB's server-formatted
// grid cells (which are deliberately left alone; see displayDate.js).
//
// The two local edge cases are KEPT rather than delegated, because `isDate` at the
// call site below is a KEY-NAME heuristic (/date|time|when|stamp/), not a declared
// type: (1) a falsy-but-present value stays blank instead of becoming 12/31/1969,
// and (2) an unparseable value is ECHOED — a mis-detected text column such as
// "statusTime" must not silently render blank and lose data on screen.
const fmtDate = (v) => (v ? fmtDisplayDate(v) || v : '')
const isGuid = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(s)

const looksLikeMoney = (key) => /(amount|total|fee|expense|paid|balance|cost|revenue|commission|production|origination|share|sum|billed|collected|allocation|trust|retainer|price)/i.test(key)
const looksLikeDate = (key) => /(date|time|when|stamp)/i.test(key)
const looksLikePercent = (key) => /(percent|percentage|pct)/i.test(key)
const looksLikeId = (key) => /^id$|Id$|^objectId$|userId$|clientId$|matterId$/i.test(key) || key.toLowerCase().endsWith('id')
const looksLikeNumber = (key) => /(quantity|hours|count|days|number|qty)/i.test(key)

const labelFromKey = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

export const looksLikeMoneyKey = looksLikeMoney

export const makeAutoColumns = (rows) => {
  if (!rows.length) return []
  const seen = new Set()
  const cols = []
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (seen.has(k)) continue
      seen.add(k)
      cols.push(k)
    }
  }
  return cols
    .filter((k) => !looksLikeId(k)) // hide raw GUIDs
    .filter((k) => {
      // Hide columns that are entirely null/empty across the dataset
      return rows.some((r) => r[k] != null && r[k] !== '')
    })
    .map((k) => {
      const isMoney = looksLikeMoney(k)
      const isDate = looksLikeDate(k)
      const isPercent = looksLikePercent(k)
      const isNum = isMoney || isPercent || looksLikeNumber(k)
      return {
        key: k,
        label: labelFromKey(k),
        align: isNum ? 'right' : 'left',
        sum: isMoney || looksLikeNumber(k),
        render: (row) => {
          const v = row[k]
          if (v == null || v === '') return ''
          if (typeof v === 'string' && isGuid(v)) return ''
          if (isDate) return fmtDate(v)
          if (isMoney) return fmtMoney(v)
          if (isPercent) return typeof v === 'number' ? `${v.toFixed(2)}%` : `${v}%`
          if (isNum) return fmtNumber(v)
          if (typeof v === 'object') return JSON.stringify(v)
          return String(v)
        },
      }
    })
}
